// RdColumnFilter.tsx — Redesign-nativer Spaltenfilter. Immer sichtbares
// Filter-Icon im Header (kein Hover-Verstecken) + Popover je Filtertyp.
// Wertevertrag identisch zum alten components/ColumnFilter.tsx, damit die
// Backend-Filter-Keys unverändert bleiben (Typ/Name/Ort/…/boTeamIds/foUserIds).

import * as React from "react";
import { RdPopover } from "./RdPopover";
import { LupeIcon } from "./tabellenIcons";
import {
  DATE_PRESETS,
  DATE_PRESETS_RUECKBLICK,
  type DatePresetKey,
  type DatePresetOption,
  type DateRangeValue,
  datePresetRange,
  filterPeopleOptions,
  isDateRangeActive,
  isPeopleActive,
  matchingDatePreset,
  normalizeDateRange,
  type PeopleRoleOption,
  teilePersonenAuswahl,
  togglePerson,
  toggleRolle,
} from "./filterModel";

export type { DateRangeValue } from "./filterModel";

export type BoFoOption = { id: string; label: string };

/** Option für den `people`-Filter (durchsuchbare User-Mehrfachauswahl). */
export type PeopleFilterOption = { id: string; label: string; photoUrl?: string; initials?: string };

export type ColumnFilterDescriptor =
  | { kind: "text" }
  // `options` sind die WERTE, die ans Backend gehen; `optionLabel` beschriftet
  // sie abweichend, wo Anzeige und Wert auseinanderfallen („Person" → „Privat").
  // `optionIndent` rückt eine Zeile ein: sie gehört zu der Option darüber
  // (Kontaktstatus: „Sonstige" und darunter die Status, aus denen die Gruppe
  // besteht). Rein optisch — am Wert ändert sich nichts.
  | {
      kind: "dropdown";
      options: string[];
      optionLabel?: (option: string) => string;
      optionIndent?: (option: string) => boolean;
    }
  | { kind: "boolean"; label: string }
  | { kind: "boFo"; boOptions: BoFoOption[]; foOptions: BoFoOption[] }
  // `rueckblick`: Schnellauswahl für Vergangenes (Heute / Letzte 7 / 30 Tage /
  // Dieses Jahr) statt der vorausschauenden (Morgen, Nächste Woche).
  | { kind: "dateRange"; rueckblick?: boolean }
  // `roles` (optional, T-245): zusätzlich die Rolle wählen, in der die Person
  // beteiligt sein muss (z. B. nur „Hauptverantwortlich"). Keine Rolle = alle.
  | { kind: "people"; options: PeopleFilterOption[]; roles?: PeopleRoleOption[] }
  // Eigener Inhalt im Popover (z. B. K-Status mit Bereich/Rolle). Der Wert
  // bleibt ein String wie bei `dropdown`; `render` bekommt ihn samt Setter.
  // `active` (optional): ob die Lupe „an" leuchtet, wenn der Inhalt
  // weitere Werte führt als den einen String (Betreff + Austausch der
  // Vorgangsliste). Fehlt er, zählt nur `value`.
  | {
      kind: "custom";
      width?: number;
      active?: boolean;
      render: (args: {
        value: string | undefined;
        onChange: (value: string | undefined) => void;
        close: () => void;
      }) => React.ReactNode;
    };


const ChevronIcon = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 4.5 6 7.5 9 4.5" />
  </svg>
);

export type RdColumnFilterProps = {
  label: string;
  descriptor: ColumnFilterDescriptor;
  /** Wert für text/dropdown/boolean. */
  value?: string | boolean;
  onChange?: (value: string | boolean | undefined) => void;
  /** Werte für boFo. */
  boFoValue?: { boTeamIds: string[]; foUserIds: string[] };
  onBoFoChange?: (next: { boTeamIds: string[]; foUserIds: string[] }) => void;
  /** Wert für dateRange (von/bis, beide optional). */
  dateRangeValue?: DateRangeValue;
  onDateRangeChange?: (next: DateRangeValue) => void;
  /** Wert für people (gewählte User-Ids). */
  peopleValue?: string[];
  onPeopleChange?: (next: string[]) => void;
};

export function RdColumnFilter({
  label,
  descriptor,
  value,
  onChange,
  boFoValue,
  onBoFoChange,
  dateRangeValue,
  onDateRangeChange,
  peopleValue,
  onPeopleChange,
}: RdColumnFilterProps) {
  const boSel = boFoValue?.boTeamIds ?? [];
  const foSel = boFoValue?.foUserIds ?? [];
  const active =
    descriptor.kind === "boFo"
      ? boSel.length + foSel.length > 0
      : descriptor.kind === "dateRange"
        ? isDateRangeActive(dateRangeValue)
        : descriptor.kind === "people"
          ? isPeopleActive(peopleValue)
          : descriptor.kind === "custom" && descriptor.active !== undefined
            ? descriptor.active
            : value !== undefined && value !== "" && value !== false;

  // dateRange braucht Platz für zwei Schnellauswahl-Buttons nebeneinander
  // („Nächste Woche" soll nicht umbrechen).
  const popWidth =
    descriptor.kind === "custom"
      ? descriptor.width ?? 264
      : descriptor.kind === "boFo" || descriptor.kind === "people"
        ? 264
        : descriptor.kind === "dateRange"
          ? 268
          : 224;

  return (
    <RdPopover
      align="end"
      width={popWidth}
      trigger={({ toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          className={"kt-filter" + (active ? " on" : "")}
          title={`${label} filtern`}
          aria-label={`${label} filtern`}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
        >
          {LupeIcon}
        </button>
      )}
    >
      {(close) => (
        <div className="rd-pop-body" onClick={(e) => e.stopPropagation()}>
          {descriptor.kind === "text" && (
            <TextFilter label={label} value={typeof value === "string" ? value : ""} onChange={onChange} close={close} />
          )}
          {descriptor.kind === "dropdown" && (
            <div className="rd-pop-list">
              {descriptor.options.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  // Aktiv-Vergleich und `onChange` laufen über den ROHWERT —
                  // beschriftet wird nur die Zeile.
                  className={
                    "rd-pop-opt" +
                    (opt === value ? " on" : "") +
                    (descriptor.optionIndent?.(opt) ? " sub" : "")
                  }
                  onClick={() => {
                    onChange?.(opt);
                    close();
                  }}
                >
                  {descriptor.optionLabel ? descriptor.optionLabel(opt) : opt}
                </button>
              ))}
              {active && (
                <button type="button" className="rd-pop-reset" onClick={() => { onChange?.(undefined); close(); }}>
                  Filter zurücksetzen
                </button>
              )}
            </div>
          )}
          {descriptor.kind === "custom" &&
            descriptor.render({
              value: typeof value === "string" && value !== "" ? value : undefined,
              onChange: (v) => onChange?.(v),
              close,
            })}
          {descriptor.kind === "boolean" && (
            <button
              type="button"
              className={"rd-pop-toggle" + (value === true ? " on" : "")}
              onClick={() => onChange?.(value === true ? undefined : true)}
            >
              {descriptor.label}
            </button>
          )}
          {descriptor.kind === "boFo" && (
            <BoFoFilter
              boOptions={descriptor.boOptions}
              foOptions={descriptor.foOptions}
              boSel={boSel}
              foSel={foSel}
              onBoFoChange={onBoFoChange}
              close={close}
            />
          )}
          {descriptor.kind === "dateRange" && (
            <DateRangeFilter
              value={dateRangeValue}
              onChange={onDateRangeChange}
              close={close}
              presets={descriptor.rueckblick ? DATE_PRESETS_RUECKBLICK : DATE_PRESETS}
            />
          )}
          {descriptor.kind === "people" && (
            <PeopleFilter
              options={descriptor.options}
              roles={descriptor.roles}
              selected={peopleValue ?? []}
              onChange={onPeopleChange}
              close={close}
            />
          )}
        </div>
      )}
    </RdPopover>
  );
}

function DateRangeFilter({
  value,
  onChange,
  close,
  presets,
}: {
  value?: DateRangeValue;
  onChange?: (next: DateRangeValue) => void;
  close: () => void;
  presets: ReadonlyArray<DatePresetOption>;
}) {
  const norm = normalizeDateRange(value);
  const aktiv = isDateRangeActive(value);
  const preset = matchingDatePreset(value, new Date(), presets);
  // Der Von/Bis-Block startet zu — außer der gesetzte Bereich stammt von Hand
  // (kein Preset trifft): dann wäre sonst nirgends sichtbar, wonach gefiltert
  // wird. Der Popover-Inhalt wird beim Schließen ausgehängt, die Entscheidung
  // fällt also bei jedem Öffnen neu.
  const [weitere, setWeitere] = React.useState(preset === null && aktiv);
  const [von, setVon] = React.useState(norm.von ?? "");
  const [bis, setBis] = React.useState(norm.bis ?? "");
  React.useEffect(() => {
    const n = normalizeDateRange(value);
    setVon(n.von ?? "");
    setBis(n.bis ?? "");
  }, [value]);
  const apply = () => {
    onChange?.(normalizeDateRange({ von, bis }));
    close();
  };
  const reset = () => {
    setVon("");
    setBis("");
    onChange?.({});
    close();
  };
  const waehlePreset = (key: DatePresetKey) => {
    onChange?.(datePresetRange(key));
    close();
  };
  return (
    <>
      <div className="rd-pop-presets">
        {presets.map((p) => (
          <button
            key={p.key}
            type="button"
            className={"rd-pop-opt" + (p.key === preset ? " on" : "")}
            onClick={() => waehlePreset(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        className={"rd-pop-more" + (weitere ? " on" : "")}
        aria-expanded={weitere}
        onClick={() => setWeitere((w) => !w)}
      >
        {ChevronIcon}
        Weitere Optionen
      </button>
      {weitere ? (
        <>
          <label className="rd-pop-fieldlabel">Von</label>
          <input className="rd-pop-input" type="date" value={von} max={bis || undefined} onChange={(e) => setVon(e.target.value)} />
          <label className="rd-pop-fieldlabel">Bis</label>
          <input className="rd-pop-input" type="date" value={bis} min={von || undefined} onChange={(e) => setBis(e.target.value)} />
          <div className="rd-pop-foot">
            <button type="button" className="rd-pop-btn" onClick={reset}>
              Reset
            </button>
            <button type="button" className="rd-pop-btn primary" onClick={apply}>
              Filtern
            </button>
          </div>
        </>
      ) : (
        // Ohne den Von/Bis-Block gäbe es sonst keinen Weg, einen per Klick
        // gesetzten Preset wieder loszuwerden (Muster wie im dropdown-Zweig).
        aktiv && (
          <button type="button" className="rd-pop-reset" onClick={reset}>
            Filter zurücksetzen
          </button>
        )
      )}
    </>
  );
}

function PeopleFilter({
  options,
  roles,
  selected,
  onChange,
  close,
}: {
  options: PeopleFilterOption[];
  roles?: PeopleRoleOption[];
  selected: string[];
  onChange?: (next: string[]) => void;
  close: () => void;
}) {
  const [needle, setNeedle] = React.useState("");
  const list = filterPeopleOptions(options, needle);
  const toggle = (id: string) => onChange?.(togglePerson(selected, id));
  const gewaehlteRollen = teilePersonenAuswahl(selected).rollen;
  return (
    <>
      {roles && roles.length > 0 && (
        <>
          <div className="rd-pop-grouptitle">Rolle</div>
          <div className="rd-pop-rollen" role="group" aria-label="Rolle">
            {roles.map((r) => {
              const an = gewaehlteRollen.includes(r.id);
              return (
                <button
                  key={r.id}
                  type="button"
                  aria-pressed={an}
                  className={"rd-pop-toggle" + (an ? " on" : "")}
                  onClick={() => onChange?.(toggleRolle(selected, r.id))}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
          <div className="rd-pop-grouptitle">Person</div>
        </>
      )}
      <input className="rd-pop-input" placeholder="Person suchen …" value={needle} onChange={(e) => setNeedle(e.target.value)} />
      <div className="rd-pop-scroll">
        {list.map((o) => (
          <label key={o.id} className="rd-pop-check">
            <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} />
            <span className="rd-pop-avatar" aria-hidden>
              {o.photoUrl ? <img src={o.photoUrl} alt="" /> : <span>{o.initials ?? o.label.slice(0, 2).toUpperCase()}</span>}
            </span>
            <span className="rd-pop-avatar-label">{o.label}</span>
          </label>
        ))}
        {list.length === 0 && <div className="rd-pop-empty">Keine Person gefunden</div>}
      </div>
      <div className="rd-pop-foot">
        <button type="button" className="rd-pop-btn" onClick={() => { onChange?.([]); close(); }}>
          Reset
        </button>
        <button type="button" className="rd-pop-btn primary" onClick={close}>
          Fertig
        </button>
      </div>
    </>
  );
}

function TextFilter({
  label,
  value,
  onChange,
  close,
}: {
  label: string;
  value: string;
  onChange?: (v: string | undefined) => void;
  close: () => void;
}) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => setV(value), [value]);
  const apply = () => {
    onChange?.(v.trim() || undefined);
    close();
  };
  return (
    <>
      <input
        className="rd-pop-input"
        autoFocus
        placeholder={`${label} filtern …`}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") apply();
        }}
      />
      <div className="rd-pop-foot">
        <button type="button" className="rd-pop-btn" onClick={() => { setV(""); onChange?.(undefined); close(); }}>
          Reset
        </button>
        <button type="button" className="rd-pop-btn primary" onClick={apply}>
          Filtern
        </button>
      </div>
    </>
  );
}

function BoFoFilter({
  boOptions,
  foOptions,
  boSel,
  foSel,
  onBoFoChange,
  close,
}: {
  boOptions: BoFoOption[];
  foOptions: BoFoOption[];
  boSel: string[];
  foSel: string[];
  onBoFoChange?: (next: { boTeamIds: string[]; foUserIds: string[] }) => void;
  close: () => void;
}) {
  const [needle, setNeedle] = React.useState("");
  const n = needle.trim().toLowerCase();
  const flt = (opts: BoFoOption[]) => opts.filter((o) => !n || o.label.toLowerCase().includes(n));
  const toggleBo = (id: string) =>
    onBoFoChange?.({ boTeamIds: boSel.includes(id) ? boSel.filter((x) => x !== id) : [...boSel, id], foUserIds: foSel });
  const toggleFo = (id: string) =>
    onBoFoChange?.({ boTeamIds: boSel, foUserIds: foSel.includes(id) ? foSel.filter((x) => x !== id) : [...foSel, id] });
  return (
    <>
      <input className="rd-pop-input" placeholder="Suchen …" value={needle} onChange={(e) => setNeedle(e.target.value)} />
      <div className="rd-pop-scroll">
        <div className="rd-pop-grouptitle">Backoffice (Team)</div>
        {flt(boOptions).map((o) => (
          <label key={o.id} className="rd-pop-check">
            <input type="checkbox" checked={boSel.includes(o.id)} onChange={() => toggleBo(o.id)} />
            {o.label}
          </label>
        ))}
        <div className="rd-pop-grouptitle">Frontoffice (Person)</div>
        {flt(foOptions).map((o) => (
          <label key={o.id} className="rd-pop-check">
            <input type="checkbox" checked={foSel.includes(o.id)} onChange={() => toggleFo(o.id)} />
            {o.label}
          </label>
        ))}
      </div>
      <div className="rd-pop-foot">
        <button type="button" className="rd-pop-btn" onClick={() => { onBoFoChange?.({ boTeamIds: [], foUserIds: [] }); close(); }}>
          Reset
        </button>
        <button type="button" className="rd-pop-btn primary" onClick={close}>
          Fertig
        </button>
      </div>
    </>
  );
}
